import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Cursor-anchored explanation box, shared by the Client Reporting sidebar
 * widgets (equity donut, stock watchlist, world markets).
 *
 * It is portalled to <body> so no panel's `overflow` or `transform` can clip it
 * or silently re-root its `position: fixed`. The caller supplies an estimated
 * size, which is only used to decide whether to flip the box to the other side
 * of the cursor near a viewport edge — an approximation is fine, and being a
 * little wrong just means the box sits slightly closer to the edge than
 * intended, never off-screen.
 */

const GAP = 14;
const EDGE = 12;

export function clampTipPosition(x, y, width, height) {
  const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 800 : window.innerHeight;

  // Prefer down-and-right of the cursor; flip rather than run off an edge.
  let left = x + GAP;
  if (left + width + EDGE > vw) left = Math.max(EDGE, x - GAP - width);

  let top = y + GAP;
  if (top + height + EDGE > vh) top = Math.max(EDGE, y - GAP - height);

  return { left, top };
}

/**
 * @param {{width?: number, height?: number}} size estimated tip box size, used
 *   only for the viewport edge flip.
 */
export function useHoverTip({ width = 220, height = 84 } = {}) {
  const [activeKey, setActiveKey] = useState(null);
  const [position, setPosition] = useState(null);

  // Pointer path: the box tracks the cursor, so it stays beside whatever the
  // reader is actually pointing at.
  const showAtCursor = useCallback((key, event) => {
    setActiveKey(key);
    if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
      setPosition(clampTipPosition(event.clientX, event.clientY, width, height));
    }
  }, [width, height]);

  // Keyboard path: no cursor to anchor to, so hang it off the row itself.
  const showAtElement = useCallback((key, event) => {
    setActiveKey(key);
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    if (rect) setPosition(clampTipPosition(rect.left + 24, rect.bottom, width, height));
  }, [width, height]);

  const clear = useCallback(() => {
    setActiveKey(null);
    setPosition(null);
  }, []);

  // Spread onto a row to make it explain itself by hover and by keyboard alike.
  const hoverProps = useCallback((key) => ({
    onMouseEnter: (event) => showAtCursor(key, event),
    onMouseMove: (event) => showAtCursor(key, event),
    onMouseLeave: clear,
    onFocus: (event) => showAtElement(key, event),
    onBlur: clear,
  }), [showAtCursor, showAtElement, clear]);

  return {
    activeKey, position, showAtCursor, showAtElement, clear, hoverProps,
  };
}

export function HoverTip({
  position,
  width = 220,
  className = '',
  children,
}) {
  if (!position || !children || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className={`vd-ml-hover-tip${className ? ` ${className}` : ''}`}
      style={{ left: position.left, top: position.top, maxWidth: width }}
      /* The row that opened it already carries the same text in its
         aria-label, so exposing the box too would just read it twice. */
      aria-hidden="true"
    >
      {children}
    </div>,
    document.body,
  );
}
